import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createEvaluatorFromCriteria } from "../api";
import type { FailureMode } from "../types";
import { penaltyCriteriaForMode } from "../utils/penaltyCriteria";

export type CreateEvaluatorStatus = "idle" | "loading" | "error";

interface UseCreateEvaluatorFromModeResult {
  status: CreateEvaluatorStatus;
  create: (mode: FailureMode) => Promise<void>;
}

// How long the error status lingers on the button before resetting to idle.
const ERROR_RESET_MS = 4000;

/**
 * Promote a failure mode into an evaluator (#1399): build the "penalize
 * responses" criterion from the mode, `POST` a new evaluator, then navigate to
 * its detail page so the user can refine the seeded wording (and grade against
 * it like any other criterion).
 *
 * Success and failure also raise a toast, matching the rest of the evaluators
 * flow (`useEvaluators.handleCreate`) — a failed *create* mutates intent and is
 * more consequential than the copy-brief action, so it warrants more than the
 * inline button label alone.
 *
 * `status` drives the button label. On success we route to `/evaluators/{id}`,
 * which unmounts the panel — so the button never has to return to idle. The
 * error reset is cancelled on unmount so it never sets state after teardown
 * (mirrors `useAgentBriefCopy`).
 */
export function useCreateEvaluatorFromMode(): UseCreateEvaluatorFromModeResult {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CreateEvaluatorStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const create = useCallback(
    async (mode: FailureMode) => {
      clearTimeout(timer.current);
      setStatus("loading");
      try {
        const criteria = penaltyCriteriaForMode(mode);
        const evaluator = await createEvaluatorFromCriteria(criteria);
        toast.success("Evaluator created");
        // Land on the new evaluator's detail page — this unmounts the panel, so
        // there is nothing to reset back to idle.
        navigate(`/evaluators/${encodeURIComponent(evaluator.group_id)}`);
      } catch (err) {
        console.error("Failed to create evaluator from failure mode:", err);
        // Surface the server's reason. `createEvaluatorFromCriteria` routes its
        // errors through `toFriendlyApiError`, so `message` is already the API's
        // `detail` (or a curated line for the auth statuses) — swallowing it in
        // favour of a fixed string leaves the user with an unactionable toast.
        toast.error(
          err instanceof Error && err.message
            ? err.message
            : "Failed to create evaluator",
        );
        setStatus("error");
        timer.current = setTimeout(() => setStatus("idle"), ERROR_RESET_MS);
      }
    },
    [navigate],
  );

  return { status, create };
}
